import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe } from 'i18n';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { User } from 'shared';

import { FunnelService } from '../../../services/funnel.service';
import {
  FAMILY_BUNDLE_PLAN_CODES, FAMILY_PLAN_SLUG, FunnelIntent, PLAN_CODE_BY_SLUG, clearFamilyToken, currentFamilyToken,
  currentReferral, normaliseIntent, readStoredIntent, rememberFamilyToken, rememberReferral, validSponsorId, wizardQuery
} from '../../../services/funnel-intent';
import { FAMILY_ACCEPT_ERRORS } from '../../../services/family.service';
import { TranslateService } from 'i18n';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/** Mirrors the backend's MIN_PASSWORD_LENGTH. */
const MIN_PASSWORD_LENGTH = 8;

type Phase = 'opening' | 'password' | 'signing_in' | 'error';

/**
 * Magic-link landing of the "ONE membership" funnel (/join/start?token=…).
 *
 * Consuming the token confirms the address and sets the soft-login cookie. That
 * cookie alone is enough for the purchase wizard, but it leaves the member
 * without a password and therefore without a way back in — so this page is where
 * they set one and get a real session:
 *
 *   token -> set password -> ordinary login -> purchase wizard
 *
 * A member who already has a password skips the middle two steps; asking them to
 * re-invent a password they already chose would be the wrong question.
 *
 * `ref` (the referral the mail was sent under) is remembered as the newest touch
 * and the newest referral goes to the backend with the token (last touch until the
 * first confirmed full payment). `from=checkout` marks the confirmation mail of an
 * account the direct checkout created: that purchase may already be paid, so the
 * member lands on the dashboard (which shows an unfinished purchase on its own),
 * never on the wizard. The
 * counterpart in the retired Health Finder funnel is funnel-quiz-start-page,
 * which instead opened a quiz session.
 */
@Component({
  selector: 'app-funnel-join-start-page',
  standalone: true,
  imports: [FormsModule, RouterLink, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-join-start-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelJoinStartPageComponent {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private http       = inject(HttpClient);
  private funnel     = inject(FunnelService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** DNA Family SPECIAL: the invitation the sign-in link carries (`family`), if any. */
  private familyToken: string | null = null;
  /** Where the backend says the accepted family purchase continues (`family_next`). */
  private familyNext: string | null = null;
  /** The backend could not bind the invitation (`family_error`): explained instead of the wizard. */
  private familyError: string | null = null;
  /** The error screen offers the dashboard (the member is signed in by then). */
  showDashboardLink = signal(false);

  readonly minLength = MIN_PASSWORD_LENGTH;

  phase = signal<Phase>('opening');
  error = signal<string | null>(null);
  busy  = signal(false);

  firstName = signal<string | null>(null);

  password = '';
  passwordConfirmation = '';

  /** Kept as plain fields + an explicit signal so the template can react to typing. */
  private touched = signal(0);
  passwordValid = computed(() => {
    this.touched();
    return this.password.length >= MIN_PASSWORD_LENGTH &&
           this.password === this.passwordConfirmation;
  });

  onPasswordInput(): void { this.touched.update(n => n + 1); }

  constructor() {
    const token = this.route.snapshot.queryParamMap.get('token')
               ?? this.route.snapshot.params['token']
               ?? '';

    if (!token) {
      this.phase.set('error');
      this.error.set('Missing confirmation token.');
      return;
    }

    // A family sign-in link (`family=<token>`, plan dna_family_special): the
    // invitation is bound to this account by the server; no referral travels with
    // it — a family purchase never changes the sponsor.
    const query = this.route.snapshot.queryParamMap;
    const familyLink = query.get('family');
    rememberFamilyToken(familyLink);
    const wantsFamily = !!familyLink || (query.get('plan') ?? '').toLowerCase() === FAMILY_PLAN_SLUG;
    this.familyToken = wantsFamily ? currentFamilyToken(familyLink) : null;

    const ref = this.familyToken ? null : validSponsorId(query.get('ref'));
    if (ref) rememberReferral(ref);

    this.funnel.startMembershipFunnel(token, this.familyToken ? null : currentReferral(ref), this.familyToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.firstName.set(res.first_name ?? null);
          // Always 200: the family outcome rides along. Accepted -> the token is
          // spent, on into the wizard; refused -> keep the token (the link may be
          // opened again) and explain once the session exists (#goToWizard).
          if (this.familyToken) {
            if (res.family_error) {
              this.familyError = res.family_error;
            } else if (res.family_status === 'accepted') {
              this.familyNext = res.family_next?.startsWith('/') ? res.family_next : null;
              clearFamilyToken();
            }
          }
          // Existing account: they already chose a password, so asking them to
          // invent another one would be the wrong question. Their session still
          // has to be built on the client before the (protected) wizard route will
          // let them through — see #adoptSession.
          if (res.account_exists) {
            this.phase.set('signing_in');
            this.adoptSession().subscribe({
              next:  () => this.goToWizard(),
              error: () => this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true })
            });
            return;
          }
          this.phase.set('password');
        },
        error: err => {
          this.phase.set('error');
          const code: string | undefined = err?.error?.code;
          this.error.set(code && (FAMILY_ACCEPT_ERRORS.includes(code) || code === 'family_invitation_required')
            ? this.translate.translate(`family_join.errors.${code}`)
            : (err?.error?.error || 'Your link is invalid or has expired.'));
        }
      });
  }

  submitPassword(): void {
    if (!this.passwordValid() || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);
    const password = this.password;

    this.funnel.setMembershipPassword(password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.phase.set('signing_in');
          // Log in properly rather than riding the funnel cookie: that yields the
          // normal session, user state and storage the rest of the app expects.
          this.auth.login({ username: res.email, password })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              // login() seeds the auth state itself, so the wizard's guard passes.
              next: () => { this.busy.set(false); this.goToWizard(); },
              error: () => {
                // The password is set — only the automatic sign-in failed, so send
                // them to the login page instead of stranding them here.
                this.busy.set(false);
                this.router.navigate(['/', this.lang(), 'login'], { replaceUrl: true });
              }
            });
        },
        error: err => {
          this.busy.set(false);
          this.phase.set('password');
          this.error.set(err?.error?.error || 'Could not set your password. Please try again.');
        }
      });
  }

  /**
   * Turns the soft-login cookie into a client-side session.
   *
   * The cookie authenticates API calls, but AuthService knows nothing about it,
   * so `authGuard` sees a logged-out visitor and bounces every protected route to
   * /login — which is what made this page flash and disappear. Fetching /user with
   * the cookie and seeding the auth state is what the impersonation exit does too;
   * the token stays null because the cookie is the credential.
   */
  private adoptSession(): Observable<unknown> {
    // Errors deliberately propagate: the caller sends them to the login page
    // rather than dropping them on a protected route they cannot enter.
    return this.http.get<{ user: User }>('/user').pipe(
      switchMap(res => from(this.auth.setAuthState(res.user, null)))
    );
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }

  /**
   * Website V2: the entry chosen on the site travels in the magic link
   * (`?plan=weekly&weeks=12`, see the backend's GlobalHealthFunnelConfig
   * .magic_link_query). Falls back to what this tab remembered from the join page —
   * the same browser usually opens the mail link, but not always, so the link wins.
   */
  private chosenEntry(): FunnelIntent {
    const query = this.route.snapshot.queryParamMap;
    const fromLink = normaliseIntent({ plan: query.get('plan'), weeks: query.get('weeks') });
    return fromLink.plan ? fromLink : readStoredIntent();
  }

  private goToWizard(): void {
    if (this.route.snapshot.queryParamMap.get('from') === 'checkout') {
      this.router.navigate(['/', this.lang(), 'dashboard'], { replaceUrl: true });
      return;
    }
    if (this.familyToken && this.familyError) {
      const code = this.familyError;
      this.phase.set('error');
      this.showDashboardLink.set(true);
      this.error.set(this.translate.translate(
        `family_join.errors.${FAMILY_ACCEPT_ERRORS.includes(code) ? code : 'generic'}`));
      return;
    }
    if (this.familyToken && this.familyNext) {
      this.router.navigateByUrl(`/${this.lang()}${this.familyNext}`, { replaceUrl: true });
      return;
    }
    if (this.familyToken) {
      // Straight into the family purchase; the wizard checks the server's
      // offered_family_codes and explains it when the invitation did not hold.
      this.router.navigate(['/', this.lang(), 'membership', 'apply'], {
        queryParams: wizardQuery(FAMILY_BUNDLE_PLAN_CODES[0]),
        replaceUrl:  true
      });
      return;
    }
    const entry = this.chosenEntry();
    const plan = entry.plan ? PLAN_CODE_BY_SLUG[entry.plan] : undefined;
    this.router.navigate(['/', this.lang(), 'membership', 'apply'], {
      queryParams: plan ? wizardQuery(plan, entry.weeks ?? null) : { category: 'club' },
      replaceUrl:  true
    });
  }
}
